import { useEffect, useState } from 'react';
import {
  bulkInviteKeyPickups,
  getUnitKeyPickupStatuses,
} from 'src/services/client';
import {
  ICalcomApiResult,
  IUnitKeyPickupStatus,
} from 'src/pages/Calendar/CalcomIntegration/types';

export interface IKeyPickupInviteResult {
  invitedUnits: IUnitKeyPickupStatus[];
  bookingLinkId: string;
}

export interface IUseKeyPickupInvite {
  units: IUnitKeyPickupStatus[];
  isLoadingUnits: boolean;
  unitsError: string | null;

  selectedUnitIds: Set<string>;
  toggleUnit: (unitId: string) => void;
  selectAllEligible: () => void;
  deselectAll: () => void;
  eligibleCount: number;

  selectedBookingLinkId: string | null;
  selectBookingLink: (bookingLinkId: string | null) => void;

  isSubmitting: boolean;
  submitError: string | null;
  inviteResult: IKeyPickupInviteResult | null;
  submit: () => Promise<void>;
}

/* Invitable = still Pending (InviteSent/Booked/HandedOff fall out) and has a homeowner.
 * We gate on homeOwnerFullName because the detail endpoint doesn't expose the email flag the
 * summary's invitableCount uses — closest client-side proxy to keep the two roughly in sync. */
export const isKeyPickupUnitSelectable = (
  unit: IUnitKeyPickupStatus
): boolean => {
  const hasHomeowner: boolean = (unit?.homeOwnerFullName?.trim() ?? '') !== '';

  return unit?.keyPickupStatus === 'Pending' && hasHomeowner;
};

export const useKeyPickupInvite = (propertyId: string): IUseKeyPickupInvite => {
  // Loaded units — the fetched list and its load status.
  const [units, setUnits] = useState<IUnitKeyPickupStatus[]>([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState<boolean>(true);
  const [unitsError, setUnitsError] = useState<string | null>(null);

  // Form state.
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(
    new Set()
  );
  const [selectedBookingLinkId, setSelectedBookingLinkId] = useState<
    string | null
  >(null);

  // Submission state — the invite request lifecycle and its result.
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] =
    useState<IKeyPickupInviteResult | null>(null);

  const fetchUnits = async (): Promise<void> => {
    setIsLoadingUnits(true);
    setUnitsError(null);

    const result: ICalcomApiResult<IUnitKeyPickupStatus[]> =
      await getUnitKeyPickupStatuses(propertyId);

    if (!result.ok) {
      setUnitsError(result.error ?? 'Failed to load units');
      setIsLoadingUnits(false);
      return;
    }

    setUnits(result.data ?? []);
    setIsLoadingUnits(false);
  };

  useEffect(() => {
    void fetchUnits();
  }, [propertyId]);

  const toggleUnit = (unitId: string): void => {
    const unit: IUnitKeyPickupStatus | undefined = units.find(
      (candidate: IUnitKeyPickupStatus) => candidate.unitId === unitId
    );

    if (!unit || !isKeyPickupUnitSelectable(unit)) {
      return;
    }

    const next: Set<string> = new Set(selectedUnitIds);

    if (next.has(unitId)) {
      next.delete(unitId);
    } else {
      next.add(unitId);
    }

    setSelectedUnitIds(next);
  };

  const selectAllEligible = (): void => {
    const next: Set<string> = new Set<string>();

    units.forEach((unit: IUnitKeyPickupStatus) => {
      if (isKeyPickupUnitSelectable(unit)) {
        next.add(unit.unitId);
      }
    });

    setSelectedUnitIds(next);
  };

  const deselectAll = (): void => {
    setSelectedUnitIds(new Set());
  };

  const selectBookingLink = (bookingLinkId: string | null): void => {
    setSelectedBookingLinkId(bookingLinkId);
  };

  const eligibleCount: number = units.filter((unit: IUnitKeyPickupStatus) =>
    isKeyPickupUnitSelectable(unit)
  ).length;

  const submit = async (): Promise<void> => {
    setSubmitError(null);

    if (selectedUnitIds.size === 0) {
      setSubmitError('Select at least one unit to invite.');
      return;
    }

    if (!selectedBookingLinkId) {
      setSubmitError('Pick a key-pickup booking link.');
      return;
    }

    setIsSubmitting(true);

    const result: ICalcomApiResult<unknown> = await bulkInviteKeyPickups({
      unitIds: Array.from(selectedUnitIds),
      propertyBookingLinkId: selectedBookingLinkId,
    });

    setIsSubmitting(false);

    if (!result.ok) {
      setSubmitError(result.error ?? 'Failed to send key-pickup invites.');
      return;
    }

    const invitedUnits: IUnitKeyPickupStatus[] = units.filter(
      (unit: IUnitKeyPickupStatus) => selectedUnitIds.has(unit.unitId)
    );

    setInviteResult({ invitedUnits, bookingLinkId: selectedBookingLinkId });
  };

  return {
    units,
    isLoadingUnits,
    unitsError,
    selectedUnitIds,
    toggleUnit,
    selectAllEligible,
    deselectAll,
    eligibleCount,
    selectedBookingLinkId,
    selectBookingLink,
    isSubmitting,
    submitError,
    inviteResult,
    submit,
  };
};
